import { defineStore } from 'pinia';
import { API } from '@vp/common';
import { useConnectionStore } from '@/stores/connection';
import type { Mutation } from '@/generated/graphql';
import * as apolloCore from '@apollo/client/core';
import { Settlement } from '@vp/common/api';

const { gql } = apolloCore;

const SettlementApi = API.App.Settlement.Api;

/**
 * The settlement as AppSchema publishes it. This replaces a local `eventArtistSettlement` interface
 * that had drifted from both schemas: it declared `address` as an array the templates have always
 * read as one object.
 */
export type SettlementSheet = API.App.Settlement.Settlement;

interface SettlementSheetState {
  settlementSheet?: SettlementSheet;
  settlementSheetLoading: boolean;
}

const sectionError = (errors: Array<{ message: string }>): Error =>
  new Error(errors.map(error => error.message).join(', '));

const throwIfErrors = (result: { errors?: Array<{ message: string }> | null } | null | undefined): void => {
  if (result?.errors?.length) {
    throw sectionError(result.errors);
  }
};

export const useSettlementSheetStore = defineStore('settlementSheet', {
  state: (): SettlementSheetState => ({
    settlementSheet: undefined,
    settlementSheetLoading: false,
  }),

  actions: {
    async fetchSettlementSheet(settlementId: number) {
      const connectionStore = useConnectionStore();
      const gqlApiClient = connectionStore.gqlApiClient!;
      this.settlementSheetLoading = true;

      try {
        // The legacy read cloned its result through JSON to escape Apollo's frozen cache. Every
        // object the store holds now is one `fromGql` constructed, and the one value still passed
        // through by reference — the `eventIds` scalar array — is only ever copied into a fresh
        // `Set` by `useSettlementSheet`.
        this.settlementSheet = await SettlementApi.fetchSettlement(gqlApiClient, settlementId);
      } finally {
        this.settlementSheetLoading = false;
      }
    },

    async fetchSettlementArtist(settlementId: number) {
      const connectionStore = useConnectionStore();

      return SettlementApi.fetchSettlementArtist(connectionStore.gqlApiClient!, settlementId);
    },

    async fetchEventArtistSettlements(eventGroupId: number, artistId: number) {
      const connectionStore = useConnectionStore();

      return SettlementApi.fetchEventArtistSettlements(connectionStore.gqlApiClient!, {
        eventGroupId: String(eventGroupId),
        artistId: String(artistId),
      });
    },

    async createEventArtistSettlement(eventGroupId: number, artistId: number) {
      const connectionStore = useConnectionStore();

      return SettlementApi.createSettlement(connectionStore.gqlApiClient!, {
        eventGroupId: String(eventGroupId),
        artistId: String(artistId),
      });
    },

    async archiveEventArtistSettlement(settlementId: number) {
      const connectionStore = useConnectionStore();

      return SettlementApi.archiveSettlement(connectionStore.gqlApiClient!, { id: String(settlementId) });
    },

    async updateSelectedEvents(eventIds: number[]) {
      if (!this.settlementSheet) {
        return;
      }

      const connectionStore = useConnectionStore();
      const gqlApiClient = connectionStore.gqlApiClient!;
      const { id, settlementType } = this.settlementSheet;

      // The legacy patch sent `settlementType: 'TICKET_SALES_EVENTS'` alongside the ids, and it had
      // to: the model's `before_save :process_settlement_type` blanks `event_ids` on any settlement
      // not already recorded as ticket sales. `saveSettlementEvents` cannot carry the type —
      // `settlementType` is published only on `SettlementTaxDetailsInput` — so the switch back from
      // manual GBOR writes the type first, or the selection is discarded on arrival, the radio snaps
      // back, and the settlement keeps computing on the manual-GBOR basis. Publishing
      // `settlementType` on the events input would collapse this back into one round trip.
      if (settlementType !== Settlement.SettlementType.TicketSalesEvents) {
        const typeResult = await SettlementApi.saveSettlementType(
          gqlApiClient,
          id,
          Settlement.SettlementType.TicketSalesEvents,
        );

        if (!typeResult.success) {
          throw sectionError(typeResult.errors);
        }

        // The guard above reads local state, so the write has to be reflected in it — otherwise a
        // caller that does not refetch (the selection watcher does not) would re-send the type on
        // every date toggle. One scalar, assigned in place: replacing the settlement here is what
        // the note below rules out.
        if (this.settlementSheet) {
          this.settlementSheet.settlementType = Settlement.SettlementType.TicketSalesEvents;
        }
      }

      const result = await SettlementApi.saveSettlementEvents(gqlApiClient, id, eventIds);

      if (!result.success) {
        throw sectionError(result.errors);
      }

      // Deliberately assigns nothing. `SettlementConfig.vue`'s selection watcher is a caller, and
      // `useSettlementSheet` derives the selection back out of `settlementSheet.eventIds` — so an
      // assignment here re-enters the watcher, and on the two-step switch the intermediate
      // settlement still carries `eventIds: []`. The branch that needs fresh state refetches.
    },

    async saveManualTicketSales(items: API.App.Settlement.ManualTicketSaleInput[]) {
      if (!this.settlementSheet) {
        return;
      }

      const connectionStore = useConnectionStore();
      const gqlApiClient = connectionStore.gqlApiClient!;

      // Rows and type in one save, as the legacy patch sent them; two calls could fail between.
      const result = await SettlementApi.saveSettlementManualTicketSales(
        gqlApiClient,
        this.settlementSheet.id,
        items,
        Settlement.SettlementType.ManualGbor,
      );

      if (!result.success) {
        throw sectionError(result.errors);
      }

      this.settlementSheet = result.data;
    },

    async saveCustomExpenses(items: API.App.Settlement.CustomExpenseInput[]) {
      if (!this.settlementSheet) {
        return;
      }

      const connectionStore = useConnectionStore();
      const gqlApiClient = connectionStore.gqlApiClient!;

      const result = await SettlementApi.saveSettlementCustomExpenses(gqlApiClient, this.settlementSheet.id, items);

      if (!result.success) {
        throw sectionError(result.errors);
      }
    },

    async saveTaxDetails(details: API.App.Settlement.TaxDetailsInput) {
      if (!this.settlementSheet) {
        return;
      }

      const connectionStore = useConnectionStore();
      const gqlApiClient = connectionStore.gqlApiClient!;

      // The payload is built in `@vp/common`, per key: the legacy patch was presence-gated, so a
      // cleared field was dropped, and picking which keys may be sent as null is what makes
      // clearing reachable without failing the section on a validation.
      const result = await SettlementApi.saveSettlementTaxDetails(gqlApiClient, this.settlementSheet.id, details);

      if (!result.success) {
        throw sectionError(result.errors);
      }

      this.settlementSheet = result.data;
    },

    async saveDeals(deals: Settlement.DealAttributes[]) {
      if (!this.settlementSheet) {
        return;
      }

      const connectionStore = useConnectionStore();
      const gqlApiClient = connectionStore.gqlApiClient!;

      // The nine-key whitelist the legacy `take(...)` applied here lives in `@vp/common` now, where
      // it holds whatever `SettlementDeal.vue` hands over rather than depending on the caller.
      const result = await SettlementApi.saveSettlementDeals(gqlApiClient, this.settlementSheet.id, deals);

      if (!result.success) {
        throw sectionError(result.errors);
      }

      this.settlementSheet = result.data;
    },

    async savePayments(items: API.App.Settlement.PaymentInput[]) {
      if (!this.settlementSheet) {
        return;
      }

      const connectionStore = useConnectionStore();
      const gqlApiClient = connectionStore.gqlApiClient!;

      // Assigns nothing: `Payments.vue`'s `onSave` reads the payment id off the field array next.
      const result = await SettlementApi.saveSettlementPayments(gqlApiClient, this.settlementSheet.id, items);

      if (!result.success) {
        throw sectionError(result.errors);
      }
    },

    async createSettlementAttachment(attachmentId: number, paymentId: number) {
      const connectionStore = useConnectionStore();
      const gqlApiClient = connectionStore.gqlApiClient!;

      const { data } = await gqlApiClient.mutate<Mutation>({
        mutation: gql`
          mutation ($input: CreateEventArtistSettlementAttachmentMutationInput!) {
            createEventArtistSettlementAttachment(input: $input) {
              success
              errors { message path }
            }
          }
        `,
        variables: { input: { attachmentId, paymentId } },
      });
      throwIfErrors(data?.createEventArtistSettlementAttachment);
    },

    async deleteSettlementAttachment(attachmentId: number, paymentId: number) {
      const connectionStore = useConnectionStore();
      const gqlApiClient = connectionStore.gqlApiClient!;

      // The mutation requires exactly one association id (payment/deal/event-artist-detail)
      // to know what to detach — payments pass paymentId, same as createSettlementAttachment.
      const { data } = await gqlApiClient.mutate<Mutation>({
        mutation: gql`
          mutation ($input: DeleteEventArtistSettlementAttachmentMutationInput!) {
            deleteEventArtistSettlementAttachment(input: $input) {
              success
              errors { message path }
            }
          }
        `,
        variables: { input: { attachmentId, paymentId } },
      });
      throwIfErrors(data?.deleteEventArtistSettlementAttachment);
    },
  },
});
